import React, { useMemo } from 'react';
import { Row, Col, Table, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { EChartsOption } from 'echarts';
import ChartCard from '@/components/ChartCard';
import StorageDetailCharts from './StorageDetailCharts';
import { format2, sanitizeFilename } from '@/utils/charts';
import type { StorageDailyRow, AGCStorageDetailRow } from '@/types/api';

interface RevenueStorageTabProps {
  rows: StorageDailyRow[];
  detailRows?: AGCStorageDetailRow[];
  isSingleDay: boolean;
  displayStation: string;
  startDate: string;
  endDate: string;
  missingDates: string[];
  detailLoading?: boolean;
  detailError?: Error | null;
  onDetailRetry?: () => void;
}

const RevenueStorageTab: React.FC<RevenueStorageTabProps> = ({
  rows,
  detailRows,
  isSingleDay,
  displayStation,
  startDate,
  missingDates,
  detailLoading,
  detailError,
  onDetailRetry,
}) => {
  const totals = useMemo(() => {
    let totalRevenue = 0;
    let totalV = 0, totalE = 0, totalS = 0, totalA = 0;
    let totalCharge = 0, totalDischarge = 0;
    for (const r of rows) {
      totalV += Number(r['虚报挽回(元)']) || 0;
      totalE += Number(r['现货净利(元)']) || 0;
      totalS += Number(r['机制补贴(元)']) || 0;
      totalA += Number(r['规避考核(元)']) || 0;
      totalRevenue += Number(r['配储总收益(元)']) || 0;
      totalCharge += Number(r['总充电量(MWh)']) || 0;
      totalDischarge += Number(r['总放电量(MWh)']) || 0;
    }
    return { totalRevenue, totalV, totalE, totalS, totalA, totalCharge, totalDischarge };
  }, [rows]);

  const xLabels = useMemo(() => rows.map((r) => {
    const d = new Date(r['日期'] + 'T00:00:00');
    return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }), [rows]);

  const revenues = useMemo(() => rows.map((r) => Number(r['配储总收益(元)']) || 0), [rows]);
  const revenueHelpers = useMemo(() => revenues.map((_, i) => {
    if (i === 0) return 0;
    return revenues.slice(0, i).reduce((a, b) => a + b, 0);
  }), [revenues]);

  const waterfallOption: EChartsOption = useMemo(() => {
    if (!rows.length) return {};
    const categories = [...xLabels, '合计'];
    const helpers = [...revenueHelpers, 0];
    const values = [...revenues, totals.totalRevenue];
    helpers[helpers.length - 1] = 0;
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross' }, formatter: (params: any) => {
        const p = params[1] || params[0];
        if (!p) return '';
        const idx = p.dataIndex;
        const val = values[idx];
        const isTotal = idx === values.length - 1;
        return `${categories[idx]}<br/>${isTotal ? '合计' : '收益'}: ${format2(val)} 元`;
      }},
      toolbox: { show: false },
      dataZoom: { show: false },
      grid: { left: 60, right: 30, top: 30, bottom: 60 },
      xAxis: { type: 'category', data: categories, axisLabel: { fontSize: 11 } },
      yAxis: { type: 'value', name: '元', splitLine: { lineStyle: { color: '#f3f4f6' } } },
      series: [
        {
          name: '辅助',
          type: 'bar',
          stack: 'total',
          itemStyle: { color: 'transparent' },
          data: helpers,
        },
        {
          name: '收益',
          type: 'bar',
          stack: 'total',
          data: values.map((v, i) => ({
            value: v,
            itemStyle: {
              color: i === values.length - 1 ? '#0d9488' : v >= 0 ? '#0d9488' : '#ef4444',
            },
          })),
          label: { show: true, position: 'top', formatter: (p: any) => format2(p.value) },
        },
      ],
    };
  }, [rows, xLabels, revenueHelpers, revenues, totals.totalRevenue]);

  const tableColumns = [
    { title: '日期', dataIndex: 'date', key: 'date' },
    { title: '虚报挽回(元)', dataIndex: 'v', key: 'v', align: 'right' as const, render: (v: number) => format2(v) },
    { title: '虚报挽回电量(MWh)', dataIndex: 'v_q', key: 'v_q', align: 'right' as const, render: (v: number) => format2(v) },
    { title: '现货净利(元)', dataIndex: 'e', key: 'e', align: 'right' as const, render: (v: number) => format2(v) },
    { title: '机制补贴(元)', dataIndex: 's', key: 's', align: 'right' as const, render: (v: number) => format2(v) },
    { title: '规避考核(元)', dataIndex: 'a', key: 'a', align: 'right' as const, render: (v: number) => format2(v) },
    { title: '配储总收益(元)', dataIndex: 'total', key: 'total', align: 'right' as const, render: (v: number) => format2(v) },
    { title: '总充电量(MWh)', dataIndex: 'charge', key: 'charge', align: 'right' as const, render: (v: number) => format2(v) },
    { title: '总放电量(MWh)', dataIndex: 'discharge', key: 'discharge', align: 'right' as const, render: (v: number) => format2(v) },
  ];

  const tableData = useMemo(() => {
    const data = rows.map((r) => ({
      key: r['日期'],
      date: r['日期'],
      v: Number(r['虚报挽回(元)']) || 0,
      v_q: Number(r['虚报挽回电量(MWh)']) || 0,
      e: Number(r['现货净利(元)']) || 0,
      s: Number(r['机制补贴(元)']) || 0,
      a: Number(r['规避考核(元)']) || 0,
      total: Number(r['配储总收益(元)']) || 0,
      charge: Number(r['总充电量(MWh)']) || 0,
      discharge: Number(r['总放电量(MWh)']) || 0,
    }));
    const sum = {
      key: 'sum',
      date: '合计',
      v: data.reduce((s, r) => s + r.v, 0),
      v_q: data.reduce((s, r) => s + r.v_q, 0),
      e: data.reduce((s, r) => s + r.e, 0),
      s: data.reduce((s, r) => s + r.s, 0),
      a: data.reduce((s, r) => s + r.a, 0),
      total: data.reduce((s, r) => s + r.total, 0),
      charge: data.reduce((s, r) => s + r.charge, 0),
      discharge: data.reduce((s, r) => s + r.discharge, 0),
    };
    return [...data, sum];
  }, [rows]);

  const handleExportCsv = () => {
    if (!detailRows || !detailRows.length) return;
    const headers = [
      'time','pv_avail','pv_act','agc_target','agc_act','ust','bat','ep','p',
      'v','v_q','e','s','a','c_q','e_q','e_p','v_p','soc',
    ];
    type CsvRow = Record<string, string | number>;
    const lines = detailRows.map((r) => headers.map((h) => ((r as unknown) as CsvRow)[h] ?? '').join(','));
    const csv = '﻿' + [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    let url: string | null = null;
    try {
      url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${sanitizeFilename(displayStation)}_${startDate}_agc_storage测算.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      if (url) URL.revokeObjectURL(url);
    }
  };

  if (isSingleDay) {
    const dischargePrice = totals.totalDischarge > 0 ? totals.totalRevenue / totals.totalDischarge : 0;
    return (
      <div>
        {/* KPI */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>配储总收益</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{format2(totals.totalRevenue)} 元</div>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>总放电量</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{format2(totals.totalDischarge)} MWh</div>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>放电综合电价</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{format2(dischargePrice)} 元/MWh</div>
            </div>
          </Col>
        </Row>

        {/* Breakdown */}
        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 24, textAlign: 'center' }}>
          虚报挽回 {format2(totals.totalV)} 元 · 现货净利 {format2(totals.totalE)} 元 · 机制补贴 {format2(totals.totalS)} 元 · 规避考核 {format2(totals.totalA)} 元
        </div>

        {/* Charts */}
        <StorageDetailCharts
          rows={detailRows || []}
          loading={detailLoading}
          error={detailError}
          onRetry={onDetailRetry}
        />

        {/* Export */}
        <div style={{ marginTop: 16 }}>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleExportCsv}>
            导出测算数据 (CSV)
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* KPI */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>配储总收益</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{format2(totals.totalRevenue)} 元</div>
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>总充电量</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{format2(totals.totalCharge)} MWh</div>
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>总放电量</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{format2(totals.totalDischarge)} MWh</div>
          </div>
        </Col>
      </Row>

      {/* Breakdown */}
      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 24, textAlign: 'center' }}>
        虚报挽回 {format2(totals.totalV)} 元 + 现货净利 {format2(totals.totalE)} 元 + 机制补贴 {format2(totals.totalS)} 元 + 规避考核 {format2(totals.totalA)} 元
      </div>

      {/* Daily table */}
      <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 16 }}>按日收益汇总表</div>
      <Table
        columns={tableColumns}
        dataSource={tableData}
        pagination={false}
        size="small"
        bordered
      />

      {/* Waterfall */}
      <div style={{ marginTop: 24 }}>
        <ChartCard title="配储收益瀑布图" option={waterfallOption} height={450} />
      </div>

      {missingDates.length > 0 && (
        <div style={{ marginTop: 16, fontSize: 12, color: '#6b7280' }}>
          以下日期暂无数据，已跳过：{missingDates.join('，')}
        </div>
      )}
    </div>
  );
};

export default RevenueStorageTab;
